import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Edit, Play, Pause, Square, BarChart3, Clock, Layers, Users } from 'lucide-react';
import ExperimentResults from './ExperimentResults';
import ExperimentTimeSeries from './ExperimentTimeSeries';
import SegmentAnalysis from './SegmentAnalysis';
import './ExperimentDetail.css';

const TABS = [
  { key: 'overview', label: 'Overview', icon: Layers },
  { key: 'results', label: 'Results', icon: BarChart3 },
  { key: 'timeline', label: 'Timeline', icon: Clock },
  { key: 'segments', label: 'Segments', icon: Users },
];

function ExperimentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [experiment, setExperiment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchExperiment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/experiments/${id}`);
      setExperiment(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch experiment:', err);
      setError('Failed to load experiment');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchExperiment();
  }, [fetchExperiment]);

  const handleLifecycleAction = async (action) => {
    setActionLoading(true);
    try {
      await axios.post(`/api/experiments/${id}/lifecycle`, { action });
      await fetchExperiment();
    } catch (err) {
      console.error(`Failed to ${action} experiment:`, err);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="experiment-detail">
        <div className="exp-loading">
          <div className="spinner"></div>
          <p>Loading experiment...</p>
        </div>
      </div>
    );
  }

  if (error || !experiment) {
    return (
      <div className="experiment-detail">
        <div className="exp-error">{error || 'Experiment not found'}</div>
      </div>
    );
  }

  const { status } = experiment;

  return (
    <div className="experiment-detail">
      <div className="exp-detail-header">
        <div className="exp-detail-header-left">
          <h1>{experiment.name}</h1>
          <div className="exp-detail-meta">
            <span className={`exp-status-badge exp-status-${status}`}>{status}</span>
            <span className="exp-detail-key">{experiment.key}</span>
            {experiment.site && <span className="exp-detail-site">{experiment.site}</span>}
          </div>
        </div>
        <div className="exp-detail-actions">
          {status === 'draft' && (
            <button
              className="exp-btn-primary"
              disabled={actionLoading}
              onClick={() => handleLifecycleAction('start')}
            >
              <Play size={16} />
              Start
            </button>
          )}
          {status === 'running' && (
            <button
              className="exp-btn-secondary"
              disabled={actionLoading}
              onClick={() => handleLifecycleAction('pause')}
            >
              <Pause size={16} />
              Pause
            </button>
          )}
          {status === 'paused' && (
            <>
              <button
                className="exp-btn-primary"
                disabled={actionLoading}
                onClick={() => handleLifecycleAction('resume')}
              >
                <Play size={16} />
                Resume
              </button>
              <button
                className="exp-btn-secondary"
                disabled={actionLoading}
                onClick={() => handleLifecycleAction('complete')}
              >
                <Square size={16} />
                Complete
              </button>
            </>
          )}
          {status === 'running' && (
            <button
              className="exp-btn-secondary"
              disabled={actionLoading}
              onClick={() => handleLifecycleAction('complete')}
            >
              <Square size={16} />
              Complete
            </button>
          )}
          <button
            className="exp-btn-secondary"
            onClick={() => navigate(`/experiments/${id}/edit`)}
          >
            <Edit size={16} />
            Edit
          </button>
        </div>
      </div>

      <div className="exp-detail-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              className={`exp-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="exp-detail-content">
        {activeTab === 'overview' && (
          <div className="exp-overview">
            <div className="exp-overview-grid">
              <div className="exp-card">
                <h3>Details</h3>
                <div className="exp-detail-fields">
                  <div className="exp-field">
                    <span className="exp-field-label">Type</span>
                    <span className="exp-field-value">{experiment.type}</span>
                  </div>
                  <div className="exp-field">
                    <span className="exp-field-label">Traffic</span>
                    <span className="exp-field-value">{experiment.traffic_percentage ?? 100}%</span>
                  </div>
                  <div className="exp-field">
                    <span className="exp-field-label">Created</span>
                    <span className="exp-field-value">{formatDate(experiment.created_at)}</span>
                  </div>
                  <div className="exp-field">
                    <span className="exp-field-label">Start Date</span>
                    <span className="exp-field-value">{formatDate(experiment.start_date)}</span>
                  </div>
                  <div className="exp-field">
                    <span className="exp-field-label">End Date</span>
                    <span className="exp-field-value">{formatDate(experiment.end_date)}</span>
                  </div>
                </div>
                {experiment.description && (
                  <p className="exp-description">{experiment.description}</p>
                )}
              </div>

              <div className="exp-card">
                <h3>Variants</h3>
                <div className="exp-variant-list">
                  {(experiment.variants || []).map((v, i) => (
                    <div key={v.id || i} className="exp-variant-row">
                      <div
                        className="exp-variant-color"
                        style={{ backgroundColor: VARIANT_COLORS[i % VARIANT_COLORS.length] }}
                      />
                      <div className="exp-variant-info">
                        <span className="exp-variant-name">
                          {v.name || v.key}
                          {v.is_control && <span className="variant-control-badge">Control</span>}
                        </span>
                        <span className="exp-variant-weight">{v.weight}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="exp-card">
                <h3>Goals</h3>
                {(experiment.goals || []).length === 0 ? (
                  <p className="exp-no-data">No goals defined</p>
                ) : (
                  <div className="exp-goal-list">
                    {experiment.goals.map((g, i) => (
                      <div key={g.id || i} className="exp-goal-row">
                        <span className="exp-goal-event">{g.event_name}</span>
                        <span className="exp-goal-type">{g.goal_type}</span>
                        {g.is_primary && <span className="goal-primary-badge">Primary</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'results' && (
          <ExperimentResults experimentId={id} experiment={experiment} />
        )}

        {activeTab === 'timeline' && (
          <ExperimentTimeSeries experimentId={id} variants={experiment.variants || []} />
        )}

        {activeTab === 'segments' && (
          <SegmentAnalysis experimentId={id} variants={experiment.variants || []} />
        )}
      </div>
    </div>
  );
}

const VARIANT_COLORS = ['#8B6F4E', '#6B8E6B', '#B8860B', '#708090', '#CD853F'];

export default ExperimentDetail;
